import { useMsal } from "@azure/msal-react";
import {
    Box,
    Button,
    Flex,
    Group,
    Loader,
    Modal,
    Stack,
    TagsInput,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { ADMIN_BADGE_COLORS, StringBadges } from "src/components/adminBadges";
import { formatDateTimeForCell } from "src/components/MetadataDocuments/columns/columnHelpers";
import { PromptTemplateContentField } from "src/components/promptTemplateContentField";
import { DateRangeFilter } from "src/components/shared/DateRangeFilter";
import { useAuthSearchPagination } from "src/hooks/useAuthSearchPagination";
import type { PromptTemplate } from "src/hooks/useConfiguration";
import { deleteAPI, fetchWithAuth } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const PROMPT_TEMPLATES_URL = `${BACKEND_URL}/v1/system/prompt-templates`;

interface UpsertParams {
    data: Partial<PromptTemplate>;
}

interface DeleteParams {
    id: string;
}

const usePromptTemplateOperations = () => {
    const { instance } = useMsal();

    const upsertPromptTemplate = async ({ data }: UpsertParams) => {
        const response = await fetchWithAuth({
            url: PROMPT_TEMPLATES_URL,
            method: "PUT",
            instance,
            payload: data,
        });

        if (!response.ok) {
            throw new Error("Failed to save prompt template");
        }

        return response.json();
    };

    const deletePromptTemplate = async ({ id }: DeleteParams) => {
        const response = await deleteAPI({
            url: `${PROMPT_TEMPLATES_URL}?id=${encodeURIComponent(id)}`,
            instance,
        });

        if (!response.ok) {
            throw new Error("Failed to delete prompt template");
        }

        return response.json();
    };

    return { upsertPromptTemplate, deletePromptTemplate };
};

const ManagePromptTemplates = () => {
    const isAdmin = useAdmin();
    const queryClient = useQueryClient();

    const columns = useMemo<MRT_ColumnDef<PromptTemplate>[]>(
        () => [
            {
                accessorKey: "persona",
                header: "Persona",
                accessorFn: (row) => row?.persona ?? "",
                Cell: ({ row }) => {
                    const persona = row.original.persona ?? "";
                    return (
                        <Text size="sm" lineClamp={1} title={persona}>
                            {persona || "—"}
                        </Text>
                    );
                },
            },
            {
                accessorKey: "title",
                header: "Title",
                accessorFn: (row) => row?.title ?? "",
            },
            {
                accessorKey: "content",
                header: "Content",
                accessorFn: (row) => (row?.content ?? "").replace(/\s+/g, " ").trim(),
                Cell: ({ row }) => {
                    const content = row.original.content ?? "";
                    const preview = content.replace(/\s+/g, " ").trim();
                    return (
                        <Text size="sm" lineClamp={1} title={content}>
                            {preview}
                        </Text>
                    );
                },
            },
            {
                accessorKey: "tags",
                header: "Tags",
                accessorFn: (row) => (row ? row.tags?.join(", ") : "") ?? "",
                Cell: ({ row }) => (
                    <StringBadges
                        items={row.original.tags ?? []}
                        color={ADMIN_BADGE_COLORS.tag}
                        singleLine
                    />
                ),
            },
            {
                accessorKey: "created_by",
                header: "Created By",
                accessorFn: (row) => row?.created_by ?? row?.created_by_id ?? "",
            },
            {
                accessorKey: "last_modified_time",
                header: "Last Modified",
                accessorFn: (row) =>
                    row?.last_modified_time ? new Date(row.last_modified_time) : undefined,
                Cell: ({ cell }) => {
                    const value = cell.getValue<Date | undefined>();
                    return formatDateTimeForCell(value, "");
                },
                Filter: DateRangeFilter,
                sortingFn: "datetime",
            },
        ],
        [],
    );

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);

    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useAuthSearchPagination<PromptTemplate>({
            queryKey: ["prompt-templates-admin"],
            path: "v1/system/prompt-templates/search",
            columnFilters,
            globalFilter,
            globalFilterKey: "title",
            maxResults: 25,
            enabled: isAdmin,
        });

    // The manage view is admin-only and should surface only admin-provided templates,
    // not templates created by regular users from the chat picker.
    const promptTemplates = useMemo(() => {
        if (!data?.pages) return [];

        const seen = new Set<string>();
        return data.pages
            .flatMap((page) => page.hits.hits)
            .filter((hit) => {
                if (seen.has(hit._id)) return false;
                seen.add(hit._id);
                return true;
            })
            .map((hit) => hit._source)
            .filter((template) => template.is_admin_provided);
    }, [data]);

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isFetchingNextPage &&
                hasNextPage
            ) {
                fetchNextPage();
            }
        }
    };

    const { upsertPromptTemplate, deletePromptTemplate } = usePromptTemplateOperations();

    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            if (!editingTemplate) throw new Error("No prompt template selected");

            await deletePromptTemplate({ id: editingTemplate.id });

            notifications.show({
                title: "Success",
                message: "Prompt template deleted successfully",
                color: "green",
            });
            setDeleteModalOpened(false);
            setFormModalOpened(false);
            setEditingTemplate(null);
            form.reset();

            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["prompt-templates-admin"] });
            await queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message:
                    error instanceof Error ? error.message : "Failed to delete prompt template",
                color: "red",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const [formModalOpened, setFormModalOpened] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const form = useForm({
        initialValues: {
            title: "",
            persona: "",
            content: "",
            tags: [] as string[],
        },
        validate: {
            title: (value) => (!value ? "Title is required" : null),
            content: (value) => (!value ? "Content is required" : null),
        },
    });

    const handleOpenCreateModal = () => {
        setEditingTemplate(null);
        form.reset();
        setFormModalOpened(true);
    };

    const handleOpenEditModal = (template: PromptTemplate) => {
        setEditingTemplate(template);
        form.setValues({
            title: template.title,
            persona: template.persona ?? "",
            content: template.content,
            tags: template.tags ?? [],
        });
        setFormModalOpened(true);
    };

    const handleSave = async (values: typeof form.values) => {
        setIsSaving(true);
        try {
            const result = await upsertPromptTemplate({
                data: editingTemplate
                    ? { ...values, id: editingTemplate.id }
                    : { ...values, is_admin_provided: true },
            });

            if (!result) throw new Error("Failed to save prompt template");

            notifications.show({
                title: "Success",
                message: `Prompt template ${editingTemplate ? "updated" : "created"} successfully`,
                color: "green",
            });
            setFormModalOpened(false);
            setEditingTemplate(null);
            form.reset();

            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["prompt-templates-admin"] });
            await queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to save prompt template",
                color: "red",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const table = useMantineReactTable({
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        columns: columns as any,
        layoutMode: "grid",
        data: promptTemplates,
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${promptTemplates.length.toLocaleString()}, (Showing ${table.getFilteredRowModel().rows.length.toLocaleString()})`}
                </Text>
                {isFetching && <Loader size="xs" />}
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        enableStickyHeader: true,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenEditModal(row.original),
            style: {
                cursor: isLoading ? "wait" : "pointer",
                opacity: isLoading ? 0.7 : 1,
            },
        }),
        state: {
            isLoading,
            showProgressBars: isFetchingNextPage,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });

    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center" justify="space-between">
                    <Title lh={2} c="purple.8" order={2} tt="uppercase">
                        Manage Prompt Templates
                    </Title>
                    <Button onClick={handleOpenCreateModal} variant="filled">
                        Create New Template
                    </Button>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={formModalOpened}
                onClose={() => {
                    setFormModalOpened(false);
                    setEditingTemplate(null);
                    form.reset();
                }}
                title={editingTemplate ? "Edit Prompt Template" : "Create New Prompt Template"}
                size="lg"
            >
                <Box component="form" onSubmit={form.onSubmit(handleSave)}>
                    <Stack gap="md">
                        <TextInput
                            required
                            label="Title"
                            placeholder="e.g., Incident Summary"
                            {...form.getInputProps("title")}
                        />
                        <TextInput
                            label="Persona"
                            placeholder="Optional persona for this template"
                            {...form.getInputProps("persona")}
                        />
                        <TagsInput
                            label="Tags"
                            placeholder="Type a tag and press Enter"
                            {...form.getInputProps("tags")}
                        />
                        <PromptTemplateContentField
                            required
                            value={form.values.content}
                            onChange={(value) => form.setFieldValue("content", value)}
                            error={form.errors.content}
                        />
                        {editingTemplate ? (
                            <Group justify="flex-end" mt="md">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setFormModalOpened(false);
                                        setEditingTemplate(null);
                                        form.reset();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    color="red"
                                    onClick={() => setDeleteModalOpened(true)}
                                >
                                    Delete
                                </Button>
                                <Button type="submit" color="green" loading={isSaving}>
                                    Save Changes
                                </Button>
                            </Group>
                        ) : (
                            <Button type="submit" loading={isSaving} fullWidth>
                                Create Template
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Modal>

            <Modal
                opened={deleteModalOpened}
                onClose={() => setDeleteModalOpened(false)}
                title={`Delete Prompt Template "${editingTemplate?.title ?? ""}"?`}
                size="md"
            >
                <Box>
                    <Text mb="md">
                        Are you sure you want to delete this prompt template? This action cannot be
                        undone.
                    </Text>
                    <Flex gap="md" justify="flex-end">
                        <Button variant="outline" onClick={() => setDeleteModalOpened(false)}>
                            Cancel
                        </Button>
                        <Button color="red" loading={isDeleting} onClick={handleDelete}>
                            Delete
                        </Button>
                    </Flex>
                </Box>
            </Modal>
        </>
    );
};

export default ManagePromptTemplates;
